import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';

// Today: four tiles, each a number and seven little bars. Empties say what to
// do. A tile only shows a value dated today — a week-old sleep record is not
// "today", however recent it is in the table.

const isoDay = (d) => d.toISOString().split('T')[0];
const daysBack = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };

const Bars = ({ days, label }) => {
  const max = Math.max(...days.map((d) => d.value || 0), 1);
  return (
    <svg viewBox="0 0 140 34" preserveAspectRatio="none" className="mt-2.5 block h-[34px] w-full" aria-label={label}>
      {days.map((d, i) => {
        const h = d.value ? Math.max(3, (d.value / max) * 34) : 1;
        return (
          <rect
            key={d.date}
            x={i * 20} y={34 - h} width={16} height={h}
            fill={i === days.length - 1 && d.value ? 'var(--you)' : 'var(--line-strong)'}
          />
        );
      })}
    </svg>
  );
};

const Tile = ({ to, label, value, unit, empty, cap, days, barsLabel }) => (
  <Link to={to} className="block min-w-0 rounded-xl border border-line p-4 transition-colors hover:border-fg-3">
    <div className="text-sm text-fg-3">{label}</div>
    {value != null ? (
      <div className="mt-0.5 flex flex-wrap items-baseline gap-1.5 text-[1.75rem] font-medium leading-[1.1] tracking-[-0.025em] tabular-nums">
        {value}{unit && <small className="text-sm font-normal tracking-normal text-fg-3">{unit}</small>}
      </div>
    ) : (
      <div className="mt-0.5 text-[1.15rem] font-medium text-fg-3">{empty}</div>
    )}
    {days && days.some((d) => d.value) ? <Bars days={days} label={barsLabel} /> : <div className="mt-2.5 h-[34px] border-b border-line-strong" aria-hidden="true" />}
    <div className="mt-1 text-sm text-fg-3">{cap}</div>
  </Link>
);

const TodayCard = () => {
  const [health, setHealth] = useState(null);
  const [sleep, setSleep] = useState(null);
  const [activity, setActivity] = useState(null);
  const today = isoDay(new Date());

  const load = useCallback(async () => {
    const start = daysBack(6);
    const [h, s, a] = await Promise.allSettled([
      apiClient.get('/api/health/entries/summary', { params: { target_date: today } }),
      apiClient.get('/api/fitbit/sleep', { params: { start_date: start, end_date: today } }),
      apiClient.get('/api/fitbit/activity', { params: { start_date: start, end_date: today } }),
    ]);
    if (h.status === 'fulfilled') setHealth(h.value.data);
    if (s.status === 'fulfilled') setSleep(s.value.data);
    if (a.status === 'fulfilled') setActivity(a.value.data);
  }, [today]);

  useEffect(() => { load(); }, [load]);

  // Seven slots ending today; missing days stay empty rather than borrowing a neighbour.
  const week = Array.from({ length: 7 }, (_, i) => daysBack(6 - i));
  const series = (rows, pick) => {
    const byDate = Object.fromEntries((rows || []).map((r) => [r.date, pick(r)]));
    return week.map((date) => ({ date, value: byDate[date] || null }));
  };
  const sleepDays = series(sleep, (r) => r.total_minutes_asleep);
  const stepDays = series(activity, (r) => r.steps);
  const sleepToday = sleepDays[6].value;
  const stepsToday = stepDays[6].value;
  const kcal = health?.total_calories_consumed;
  const water = health?.total_hydration_ml;

  return (
    <section className="mt-10">
      <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-4">
        <h2 className="text-[1.2rem] font-medium tracking-[-0.015em]">Today</h2>
        <Link to="/log" className="text-sm font-medium text-fg-2 hover:text-fg">Log something →</Link>
      </div>
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Tile to="/data?tab=sleep" label="Sleep"
          value={sleepToday ? (sleepToday / 60).toFixed(1) : null} unit="hours"
          empty="Not in yet" cap={sleepToday ? 'Last 7 nights' : 'Your watch sends it later'}
          days={sleep ? sleepDays : null} barsLabel="Last 7 nights" />
        <Tile to="/data?tab=activity" label="Steps"
          value={stepsToday ? stepsToday.toLocaleString() : null}
          empty="Not in yet" cap={stepsToday ? 'Last 7 days' : 'Your watch sends it later'}
          days={activity ? stepDays : null} barsLabel="Last 7 days" />
        <Tile to="/log" label="Food"
          value={kcal ? Math.round(kcal).toLocaleString() : null} unit="kcal"
          empty="Nothing yet" cap={kcal ? 'Logged so far today' : 'Say what you ate'} />
        <Tile to="/log" label="Water"
          value={water ? Math.round(water).toLocaleString() : null} unit="ml"
          empty="Nothing yet" cap={water ? 'Logged so far today' : 'Say how much you drank'} />
      </div>
    </section>
  );
};

export default TodayCard;
